import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  getValidationSchema,
  handleErrorMessage,
  roleData,
} from "../../../../validation/profileData.validation";
import { useAuth0 } from "@auth0/auth0-react";
import useAccountProgressAPI from "../../../../hooks/useGetAccountProgressAPI";
import {
  AWARENESS_CHANNELS,
  EXPERIENCE_LEVELS,
  FIELDS_INTEREST,
  JOB_STATUS,
  POSITIONS,
  STUDY_YEARS,
} from "../../../../utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { users } from "../../../../services/api/models";
import logo from "../../../../images/cq-small-logo.png";
import { editUserDataAction } from "../../../../store/actions/AuthActions";
import { useRamadan } from "../../../../context/RamadanContext";
import carbon from "../../../../images/ramadanTheme/carbon_worship.png";
import { useAuthHelper } from "../../../../hooks/useAuthHelper";
import { getApiErrorMessage } from "../../../../utils/getApiErrorMessage";
const fieldGroups = {
  interestFieldsAndLinkedinGroup: ["interestFields", "linkedinProfileLink"],
  companyGroup: ["companyName", "experienceYears", "jobState"],
  educationGroup: ["university", "faculty"],
  locationGroup: ["country", "city"],
};
function CompleteAccountModal({ setOpenModal }) {
  const { isRamadan } = useRamadan();
  const debugProfile =
    String(process.env.REACT_APP_DEBUG_PROFILE_COMPLETION).toLowerCase() ===
    "true";

  const divRef = useRef(null);
  const awarenessChannelsRef = useRef(null);
  const { auth } = useSelector((state) => state.auth);
  const { user } = useAuth0();
  const { getSafeAccessToken } = useAuthHelper();
  const { missingFields } = useAccountProgressAPI();
  const dispatch = useDispatch();

  const [clickLoading, setClickLoading] = useState(false);
  const [openInterestFields, setOpenInterestFields] = useState(false);
  const [openAwarenessChannels, setOpenAwarenessChannels] = useState(false);
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const [jobStatusDropdownOpen, setJobStatusDropdownOpen] = useState(false);
  const [studyYearDropdownOpen, setStudyYearDropdownOpen] = useState(false);
  const [experienceYearsDropdownOpen, setExperienceYearsDropdownOpen] = useState(false);

  const SUPPORTED_FIELDS = useMemo(
    () =>
      new Set([
        // Contact/identity
        "name",
        "whatsAppPhoneNumber",
        "country",
        "city",
        // Non-client basics
        "nickname",
        "linkedinProfileLink",
        "interestFields",
        "where_you_know_us",
        "otherAwarenessChannel",
        // Position (designer/developer) — not tracked by progress hook directly,
        // but enables githubHandle/behanceProfileLink to surface in missingFields
        "position",
        // Education
        "studyYear",
        "university",
        "faculty",
        // Professional
        "companyName",
        "experienceYears",
        // Note: completion logic uses jobState, but the form field is jobStatus (mapped by roleData)
        "jobState",
        "jobStatus",
        "githubHandle",
        "behanceProfileLink",
      ]),
    []
  );

  const supportedMissingFields = useMemo(() => {
    const raw = Array.isArray(missingFields) ? missingFields : [];
    const set = new Set(raw.filter((f) => SUPPORTED_FIELDS.has(f)));

    // If awareness channels includes "Other", validate the text field too.
    if (set.has("where_you_know_us")) {
      set.add("otherAwarenessChannel");
    }

    return [...set];
  }, [missingFields, SUPPORTED_FIELDS]);

  useEffect(() => {
    if (!debugProfile) return;
    console.groupCollapsed("[CompleteAccountModal] profile completion debug");
    console.log("auth.user (subset)", {
      id: auth?.user?.id,
      auth0Id: auth?.user?.auth0Id,
      role: auth?.user?.role,
      position: auth?.user?.position,
      whatsAppPhoneNumber: auth?.user?.whatsAppPhoneNumber,
      studyYear: auth?.user?.studyYear,
      jobState: auth?.user?.jobState,
    });
    console.log("missingFields", missingFields);
    console.log("supportedMissingFields", supportedMissingFields);
    console.groupEnd();
  }, [debugProfile, auth?.user, missingFields, supportedMissingFields]);

  const validationSchema = useMemo(() => {
    // Only validate fields we actually render in this modal, otherwise RHF will
    // silently block submission and no network request will be triggered.
    const baseSchema = getValidationSchema(false, true);

    // Pick only fields present in the schema shape.
    const shapeKeys = Object.keys(baseSchema?.fields || {});
    const picked = supportedMissingFields.filter((k) => shapeKeys.includes(k));

    // If nothing to validate (shouldn't happen), fall back to no-op schema.
    if (picked.length === 0) return baseSchema.pick([]);
    return baseSchema.pick(picked);
  }, [supportedMissingFields]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      position: "",
      jobStatus: "",
      studyYear: "",
      jobState: "",
      interestFields: [],
      where_you_know_us: [],
      otherAwarenessChannel: "",
      experienceYears: "",
    },
    context: {
      submissionContext: true,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (auth.user) {
      for (const key in auth.user) {
        if (key === "interestFields" && typeof auth.user[key] === "string") {
          let interestedArray = [];

          // Check if interestFields is not an empty string
          if (auth.user.interestFields.trim() !== "") {
            // Split the string into an array and trim each field
            interestedArray = auth.user.interestFields
              .split(", ")
              .map((field) => field.trim());
          }
          setValue(key, interestedArray);
        } else if (
          key === "where_you_know_us" &&
          typeof auth.user[key] === "string"
        ) {
          let whereYouKnowUsArray = [];

          // Check if where_you_know_us is not an empty string
          if (auth.user.where_you_know_us.trim() !== "") {
            // Split the string into an array and trim each field
            whereYouKnowUsArray = auth.user.where_you_know_us
              .split(", ")
              .map((field) => field.trim());
          }
          setValue(key, whereYouKnowUsArray);
        } else {
          setValue(key, auth.user[key] || "");
        }
      }
    }
  }, [auth.user, setValue]);

  const position = watch("position");
  const jobStatus = watch("jobStatus");
  const studyYear = watch("studyYear");
  const experienceYears = watch("experienceYears");
  const whereYouKnowUs = watch("where_you_know_us");
  const isOtherSelected = whereYouKnowUs?.includes("Other");

  // Resolve effective values: prefer live form value, fall back to DB value
  const effectivePosition = position || auth.user?.position;
  // jobStatus is the form field; jobState is what the DB stores (mapped by roleData)
  const effectiveJobStatus = jobStatus || auth.user?.jobState;


  const onSubmit = async (formData) => {
    let finalWhereYouKnowUs = [
      ...formData.where_you_know_us,
      ...(formData.otherAwarenessChannel
        ? [formData.otherAwarenessChannel]
        : []),
    ];
    finalWhereYouKnowUs = finalWhereYouKnowUs.filter(
      (value) => value !== "Other",
    );

    const newDataRaw = roleData({
      // include existing user context so roleData can map correctly
      ...auth.user,
      ...formData,
      where_you_know_us: finalWhereYouKnowUs,
    });

    // Modal updates should never attempt privileged / unnecessary fields.
    const { role, id, lastLoginIP, lastLoginOn, isActive, ...newData } = newDataRaw;
    if (debugProfile) {
      console.groupCollapsed("[CompleteAccountModal] PATCH payload");
      console.log("formData", formData);
      console.log("newDataRaw", newDataRaw);
      console.log("sanitized newData", newData);
      console.groupEnd();
    }
    setClickLoading(true);
    try {
      // console.log(requestBody)
      const accessToken = await getSafeAccessToken();

      // If token is null, redirect is happening
      if (!accessToken) {
        setClickLoading(false); // Exit gracefully, redirect is happening
        return;
      }

      const headers = { Authorization: "Bearer " + accessToken };
      let Options = { headers: headers };
      const userFilter = { auth0Id: user.sub };

      const response = await users.usersPatch(newData, userFilter, Options);
      // console.log("edit user data in db : ", response.data);

      const dbUserData = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      if (response.status === 200 && dbUserData) {
        toast.success("Success update profile");
        setOpenModal(false);
        dispatch(editUserDataAction(dbUserData));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      console.log("Error in updating user data", err);
    }
    setClickLoading(false);
  };

  // Handle changes for where_you_know_us
  const handleWhereYouKnowUsChange = (event) => {
    const value = event.target.value;
    const currentValues = watch("where_you_know_us") || [];
    let newWhereYouKnowUs;

    if (event.target.checked) {
      newWhereYouKnowUs = [...currentValues, value];
    } else {
      newWhereYouKnowUs = currentValues.filter((field) => field !== value);
    }

    setValue("where_you_know_us", newWhereYouKnowUs); // Update form value
  };

  // Handle click outside
  const handleClickOutside = (event) => {
    if (divRef.current && !divRef.current.contains(event.target)) {
      setOpenInterestFields(false);
    }
  };
  const handleAwarenessChannelsClickOutside = (event) => {
    if (
      awarenessChannelsRef.current &&
      !awarenessChannelsRef.current.contains(event.target)
    ) {
      setOpenAwarenessChannels(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("mousedown", handleAwarenessChannelsClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener(
        "mousedown",
        handleAwarenessChannelsClickOutside,
      );
    };
  }, []);

  const getColumnWidth = (missingFields, fieldGroup) => {
    const missingCount = missingFields.filter((f) =>
      fieldGroup.includes(f),
    ).length;

    // For a group of 2 fields
    if (fieldGroup.length === 2) {
      if (missingCount === 1) {
        return "col-md-12";
      } else if (missingCount === 2) {
        return "col-md-6";
      }
    }

    // For a group of 3 fields
    else if (fieldGroup.length === 3) {
      if (missingCount === 1) {
        return "col-md-12";
      } else if (missingCount === 2) {
        return "col-md-6";
      } else if (missingCount === 3) {
        return "col-md-4";
      }
    }

    // Default to col-md-4 if no fields are missing (fallback)
    return "col-md-4";
  };

  return (
    <div>
      <div className="modal-open">
        <div
          className="modal"
          style={{ display: "block" }}
          role="dialog"
          tabIndex="-1"
        >
          <div className="container flex-row-fluid d-flex flex-column justify-content-center position-relative overflow-hidden p-7 mx-auto">
            <div className="d-flex justify-content-center h-100 align-items-center">
              <div
                className="authincation-content style-2"
                style={{ maxWidth: "47rem" }}
              >
                <div className="row no-gutters">
                  <div className="col-xl-12 tab-content position-relative">
                    <div
                      id="sign-in"
                      className="auth-form form-validation bg-white"
                      style={{ borderRadius: "2%", fontSize: "11px" }}
                    >
                      {isRamadan && (
                        <div
                          className="form-ramadan"
                          style={{ padding: "0px 41% 17%" }}
                        ></div>
                      )}
                      <form
                        className="form-validate step-form-horizontal"
                        onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                          console.error("CompleteAccountModal validation errors:", validationErrors);
                          const first = Object.values(validationErrors || {})[0];
                          const msg = first?.message || "Please complete the required fields.";
                          toast.error(msg);
                        })}
                      >
                        <div
                          style={{ height: "60px", marginTop: "50px" }}
                          className=" mb-5 d-flex justify-content-center align-items-center flex-column"
                        >
                          <img
                            src={logo}
                            alt="logo"
                            className="mb-4"
                            style={{ height: "80px" }}
                          />
                          <h3 className="text-primary font-w600 mb-4">
                            Complete Your Account
                          </h3>
                        </div>

                        <>
                          {supportedMissingFields.length === 0 && (
                            <div className="alert alert-info text-center mb-4" role="alert">
                              Your profile looks complete. Click <strong>Continue</strong> to go back and try submitting again.
                            </div>
                          )}

                          {/* ── Name + WhatsApp ── */}
                          <div className="row">
                            {missingFields.includes("name") && (
                              <div className={`form-group mb-3 ${missingFields.includes("whatsAppPhoneNumber") ? "col-md-6" : "col-md-12"}`}>
                                <label className="form-label font-w600">Full Name</label>
                                <input
                                  {...register("name")}
                                  type="text"
                                  className="form-control mb-1"
                                  placeholder="Please Enter Your Name (e.g. John Doe)"
                                />
                                {errors?.name && <div className="text-danger fs-12">{errors.name.message}</div>}
                              </div>
                            )}
                            {missingFields.includes("whatsAppPhoneNumber") && (
                              <div className={`form-group mb-3 ${missingFields.includes("name") ? "col-md-6" : "col-md-12"}`}>
                                <label className="form-label font-w600">WhatsApp Phone Number</label>
                                <input
                                  {...register("whatsAppPhoneNumber")}
                                  type="tel"
                                  className="form-control mb-1"
                                  placeholder="+2010xxxxxxxx"
                                />
                                {errors?.whatsAppPhoneNumber && <div className="text-danger fs-12">{errors.whatsAppPhoneNumber.message}</div>}
                              </div>
                            )}
                          </div>

                          {/* ── Nickname ── */}
                          {user.role !== "client" && missingFields.includes("nickname") && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">Nickname</label>
                              <input
                                {...register("nickname")}
                                type="text"
                                className="form-control mb-1"
                                placeholder="Enter a Unique Nickname (e.g. johndoe123)"
                              />
                              {errors?.nickname && <div className="text-danger fs-12">{errors.nickname.message}</div>}
                            </div>
                          )}

                          {/* ── Position (Designer / Developer) — show when unset for non-clients ── */}
                          {user.role !== "client" && !auth.user?.position && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">Designer OR Developer</label>
                              <input type="hidden" {...register("position")} value={position || ""} />
                              <div
                                className={`form-select form-control solid mb-1 ${positionDropdownOpen ? "dropdown-open" : ""}`}
                                onClick={() => setPositionDropdownOpen(!positionDropdownOpen)}
                                style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                              >
                                <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                  {position || "Choose..."}
                                </div>
                                {positionDropdownOpen && (
                                  <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "200px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                    {POSITIONS.map((pos, i) => (
                                      <li
                                        key={i}
                                        className="dropdown-option"
                                        onClick={(e) => { e.stopPropagation(); setValue("position", pos); setPositionDropdownOpen(false); }}
                                        style={{ padding: "0.75rem 1rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                        onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                        onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                      >
                                        {pos}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              {errors?.position && <div className="text-danger fs-12">{errors.position.message}</div>}
                            </div>
                          )}

                          {/* ── GitHub Handle (Developer only) ── */}
                          {user.role !== "client" && missingFields.includes("githubHandle") && effectivePosition === "Developer" && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">
                                Github Handle <span className="text-danger scale5 ms-2">*</span>
                              </label>
                              <input
                                {...register("githubHandle")}
                                type="url"
                                className="form-control mb-1"
                                placeholder="Enter a Unique GitHub username (e.g. john-doe)"
                              />
                              {errors?.githubHandle && <div className="text-danger fs-12">{errors.githubHandle.message}</div>}
                            </div>
                          )}

                          {/* ── Behance Profile (Designer only) ── */}
                          {user.role !== "client" && missingFields.includes("behanceProfileLink") && effectivePosition === "Designer" && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">
                                Behance <span className="text-danger scale5 ms-2">*</span>
                              </label>
                              <input
                                {...register("behanceProfileLink")}
                                type="url"
                                className="form-control mb-1"
                                placeholder="Enter a Unique Behance profile URL (e.g. bjohn-doe)"
                              />
                              {errors?.behanceProfileLink && <div className="text-danger fs-12">{errors.behanceProfileLink.message}</div>}
                            </div>
                          )}

                          {/* ── Interest Fields + LinkedIn ── */}
                          <div className="row">
                            {missingFields.includes("interestFields") && (
                              <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.interestFieldsAndLinkedinGroup)}`}>
                                <label className="form-label font-w600">Interest Fields</label>
                                <div
                                  className="form-control d-flex align-items-center mb-1"
                                  onClick={() => setOpenInterestFields(!openInterestFields)}
                                >
                                  {watch("interestFields").length === 0 ? "Choose..." : watch("interestFields").join(", ")}
                                </div>
                                {openInterestFields && (
                                  <div className="form-control h-100 p-4" ref={divRef}>
                                    {FIELDS_INTEREST.map((val, index) => (
                                      <div key={index} className="form-check">
                                        <input
                                          type="checkbox"
                                          value={val}
                                          id={`checkbox-${index}`}
                                          className="form-check-input"
                                          checked={watch("interestFields").includes(val)}
                                          {...register("interestFields")}
                                        />
                                        <label htmlFor={`checkbox-${index}`} className="form-check-label">{val}</label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {errors?.interestFields && <div className="text-danger fs-12">{errors.interestFields.message}</div>}
                              </div>
                            )}
                            {missingFields.includes("linkedinProfileLink") && (
                              <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.interestFieldsAndLinkedinGroup)}`}>
                                <label className="form-label font-w600">Linkedin Profile</label>
                                <input
                                  {...register("linkedinProfileLink")}
                                  type="url"
                                  className="form-control mb-1"
                                  placeholder="Link"
                                />
                                {errors?.linkedinProfileLink && <div className="text-danger fs-12">{errors.linkedinProfileLink.message}</div>}
                              </div>
                            )}
                          </div>

                          {/* ══ CONTESTANT fields ══ */}
                          {user.role === "contestant" && (
                            <>
                              {/* University + Faculty — shown for all contestants regardless of studyYear */}
                              {(missingFields.includes("university") || missingFields.includes("faculty")) && (
                                <div className="row">
                                  {missingFields.includes("university") && (
                                    <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.educationGroup)}`}>
                                      <label className="form-label font-w600">University</label>
                                      <input
                                        {...register("university")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your University"
                                      />
                                      {errors?.university && <div className="text-danger fs-12">{errors.university.message}</div>}
                                    </div>
                                  )}
                                  {missingFields.includes("faculty") && (
                                    <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.educationGroup)}`}>
                                      <label className="form-label font-w600">College / Faculty</label>
                                      <input
                                        {...register("faculty")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your Faculty"
                                      />
                                      {errors?.faculty && <div className="text-danger fs-12">{errors.faculty.message}</div>}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Job Status dropdown (maps to jobState via roleData) */}
                              {missingFields.includes("jobState") && (
                                <div className="form-group mb-3">
                                  <label className="form-label font-w600">Job Status</label>
                                  <input type="hidden" {...register("jobStatus")} value={jobStatus || ""} />
                                  <div
                                    className={`form-select form-control solid mb-1 ${jobStatusDropdownOpen ? "dropdown-open" : ""}`}
                                    onClick={() => setJobStatusDropdownOpen(!jobStatusDropdownOpen)}
                                    style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                                  >
                                    <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                      {jobStatus || "Choose..."}
                                    </div>
                                    {jobStatusDropdownOpen && (
                                      <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "200px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                        {JOB_STATUS.map((status, i) => (
                                          <li
                                            key={i}
                                            className="dropdown-option"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setValue("jobStatus", status);
                                              // Mirror ProfileData: auto-graduate non-students
                                              if (status !== "Student") setValue("studyYear", "Graduate");
                                              setJobStatusDropdownOpen(false);
                                            }}
                                            style={{ padding: "0.75rem 1rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                            onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                          >
                                            {status}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  {errors?.jobStatus && <div className="text-danger fs-12">{errors.jobStatus.message}</div>}
                                </div>
                              )}

                              {/* Study Year — only when job status is Student */}
                              {missingFields.includes("studyYear") && effectiveJobStatus === "Student" && (
                                <div className="form-group mb-3">
                                  <label className="form-label font-w600">Study Year</label>
                                  <input type="hidden" {...register("studyYear")} value={studyYear || ""} />
                                  <div
                                    className={`form-select form-control solid mb-1 ${studyYearDropdownOpen ? "dropdown-open" : ""}`}
                                    onClick={() => setStudyYearDropdownOpen(!studyYearDropdownOpen)}
                                    style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                                  >
                                    <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                      {studyYear || "Choose..."}
                                    </div>
                                    {studyYearDropdownOpen && (
                                      <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "200px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                        {STUDY_YEARS.map((year, i) => (
                                          <li
                                            key={i}
                                            className="dropdown-option"
                                            onClick={(e) => { e.stopPropagation(); setValue("studyYear", year); setStudyYearDropdownOpen(false); }}
                                            style={{ padding: "0.75rem 1rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                            onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                          >
                                            {year}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  {errors?.studyYear && <div className="text-danger fs-12">{errors.studyYear.message}</div>}
                                </div>
                              )}

                              {/* Employed full/part-time: Company Name + Experience Years side by side */}
                              {(effectiveJobStatus === "Employed full-time" || effectiveJobStatus === "Employed part-time") && (
                                <div className="row">
                                  {(missingFields.includes("companyName") || !auth.user?.companyName) && (
                                    <div className="form-group mb-3 col-md-6">
                                      <label className="form-label font-w600">Company Name</label>
                                      <input
                                        {...register("companyName")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your Company"
                                      />
                                      {errors?.companyName && <div className="text-danger fs-12">{errors.companyName.message}</div>}
                                    </div>
                                  )}
                                  {(missingFields.includes("experienceYears") || !(auth.user?.experienceYears > 0)) && (
                                    <div className="form-group mb-3 col-md-6">
                                      <label className="form-label font-w600">Years of experience in your field</label>
                                      <input type="hidden" {...register("experienceYears")} value={experienceYears || ""} />
                                      <div
                                        className={`form-select form-control solid mb-1 ${experienceYearsDropdownOpen ? "dropdown-open" : ""}`}
                                        onClick={() => setExperienceYearsDropdownOpen(!experienceYearsDropdownOpen)}
                                        style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                                      >
                                        <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                          {experienceYears ? `${experienceYears} year${experienceYears === "1" ? "" : "s"}` : "Choose..."}
                                        </div>
                                        {experienceYearsDropdownOpen && (
                                          <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "180px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                            {EXPERIENCE_LEVELS.map((year) => (
                                              <li
                                                key={year}
                                                className="dropdown-option"
                                                onClick={(e) => { e.stopPropagation(); setValue("experienceYears", year.toString()); setExperienceYearsDropdownOpen(false); }}
                                                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                                onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                                onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                              >
                                                {year} year{year === 1 ? "" : "s"}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                      {errors?.experienceYears && <div className="text-danger fs-12">{errors.experienceYears.message}</div>}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Freelancer / Not working: Experience Years full width */}
                              {(effectiveJobStatus === "Freelancer" || effectiveJobStatus === "Currently not working") && (missingFields.includes("experienceYears") || !(auth.user?.experienceYears > 0)) && (
                                <div className="form-group mb-3">
                                  <label className="form-label font-w600">Years of experience in field</label>
                                  <input type="hidden" {...register("experienceYears")} value={experienceYears || ""} />
                                  <div
                                    className={`form-select form-control solid mb-1 ${experienceYearsDropdownOpen ? "dropdown-open" : ""}`}
                                    onClick={() => setExperienceYearsDropdownOpen(!experienceYearsDropdownOpen)}
                                    style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                                  >
                                    <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                      {experienceYears ? `${experienceYears} year${experienceYears === "1" ? "" : "s"}` : "Choose..."}
                                    </div>
                                    {experienceYearsDropdownOpen && (
                                      <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "180px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                        {EXPERIENCE_LEVELS.map((year) => (
                                          <li
                                            key={year}
                                            className="dropdown-option"
                                            onClick={(e) => { e.stopPropagation(); setValue("experienceYears", year.toString()); setExperienceYearsDropdownOpen(false); }}
                                            style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                            onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                          >
                                            {year} year{year === 1 ? "" : "s"}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  {errors?.experienceYears && <div className="text-danger fs-12">{errors.experienceYears.message}</div>}
                                </div>
                              )}
                            </>
                          )}

                          {/* ══ NON-CONTESTANT fields (reviewer / client) ══ */}
                          {user.role !== "contestant" && (
                            <>
                              {/* University + Faculty */}
                              {(missingFields.includes("university") || missingFields.includes("faculty")) && (
                                <div className="row">
                                  {missingFields.includes("university") && (
                                    <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.educationGroup)}`}>
                                      <label className="form-label font-w600">University</label>
                                      <input
                                        {...register("university")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your University"
                                      />
                                      {errors?.university && <div className="text-danger fs-12">{errors.university.message}</div>}
                                    </div>
                                  )}
                                  {missingFields.includes("faculty") && (
                                    <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.educationGroup)}`}>
                                      <label className="form-label font-w600">Faculty</label>
                                      <input
                                        {...register("faculty")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your Faculty"
                                      />
                                      {errors?.faculty && <div className="text-danger fs-12">{errors.faculty.message}</div>}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Work experience fields */}
                              {(missingFields.includes("companyName") || missingFields.includes("experienceYears") || missingFields.includes("jobState")) && (
                                <div className={user.role !== "client" ? "row" : ""}>
                                  {missingFields.includes("companyName") && (
                                    <div className={`form-group mb-3 ${user.role !== "client" ? "col-md-4" : ""}`}>
                                      <label className="form-label font-w600">Company Name</label>
                                      <input
                                        {...register("companyName")}
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Type Your Company"
                                      />
                                      {errors?.companyName && <div className="text-danger fs-12">{errors.companyName.message}</div>}
                                    </div>
                                  )}
                                  {user.role !== "client" && missingFields.includes("experienceYears") && (
                                    <div className="form-group mb-3 col-md-4">
                                      <label className="form-label font-w600">Experience Years</label>
                                      <input type="hidden" {...register("experienceYears")} value={experienceYears || ""} />
                                      <div
                                        className={`form-select form-control solid mb-1 ${experienceYearsDropdownOpen ? "dropdown-open" : ""}`}
                                        onClick={() => setExperienceYearsDropdownOpen(!experienceYearsDropdownOpen)}
                                        style={{ cursor: "pointer", position: "relative", display: "flex", alignItems: "center" }}
                                      >
                                        <div style={{ width: "50%", display: "flex", alignItems: "center", height: "100%" }}>
                                          {experienceYears ? `${experienceYears} year${experienceYears === "1" ? "" : "s"}` : "Choose..."}
                                        </div>
                                        {experienceYearsDropdownOpen && (
                                          <ul className="dropdown-options" style={{ position: "absolute", top: "calc(100% + 8px)", left: "2.5%", width: "95%", zIndex: 1000, backgroundColor: "#fff", border: "1px solid #e6e6e6", borderRadius: "0.75rem", maxHeight: "180px", overflowY: "auto", listStyle: "none", padding: 0, margin: 0 }}>
                                            {EXPERIENCE_LEVELS.map((year) => (
                                              <li
                                                key={year}
                                                className="dropdown-option"
                                                onClick={(e) => { e.stopPropagation(); setValue("experienceYears", year.toString()); setExperienceYearsDropdownOpen(false); }}
                                                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.875rem", color: "#888888", display: "block", width: "100%", boxSizing: "border-box" }}
                                                onMouseEnter={(e) => { e.target.style.backgroundColor = "#F93A0B"; e.target.style.color = "#fff"; }}
                                                onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#888888"; }}
                                              >
                                                {year} year{year === 1 ? "" : "s"}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                      {errors?.experienceYears && <div className="text-danger fs-12">{errors.experienceYears.message}</div>}
                                    </div>
                                  )}
                                  {user.role !== "client" && missingFields.includes("jobState") && (
                                    <div className="form-group mb-3 col-md-4">
                                      <label className="form-label font-w600">Job Status</label>
                                      <select
                                        {...register("jobState")}
                                        className="form-select form-control solid mb-1"
                                      >
                                        <option value="" disabled>Choose...</option>
                                        {JOB_STATUS.map((val, i) => <option key={i} value={val}>{val}</option>)}
                                      </select>
                                      {errors?.jobState && <div className="text-danger fs-12">{errors.jobState.message}</div>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}

                          {/* ── Where You Know Us ── */}
                          {missingFields.includes("where_you_know_us") && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">Where You Know Us</label>
                              <div
                                className="form-control d-flex align-items-center mb-1"
                                onClick={() => setOpenAwarenessChannels(!openAwarenessChannels)}
                              >
                                {whereYouKnowUs.length === 0 ? "Choose..." : whereYouKnowUs.join(", ")}
                              </div>
                              {openAwarenessChannels && (
                                <div className="form-control h-100 p-4" ref={awarenessChannelsRef}>
                                  {AWARENESS_CHANNELS.map((channel, index) => (
                                    <div key={index} className="form-check">
                                      <input
                                        type="checkbox"
                                        value={channel}
                                        id={`channel-${index}`}
                                        className="form-check-input"
                                        checked={whereYouKnowUs.includes(channel)}
                                        onChange={handleWhereYouKnowUsChange}
                                      />
                                      <label htmlFor={`channel-${index}`} className="form-check-label">{channel}</label>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {errors?.where_you_know_us && <div className="text-danger fs-12">{errors.where_you_know_us.message}</div>}
                            </div>
                          )}

                          {/* ── Other Awareness Channel ── */}
                          {isOtherSelected && missingFields.includes("where_you_know_us") && (
                            <div className="form-group mb-3">
                              <label className="form-label font-w600">
                                Other <span className="text-danger scale5 ms-2">*</span>
                              </label>
                              <input
                                {...register("otherAwarenessChannel")}
                                type="text"
                                className="form-control mb-1"
                                placeholder="Other"
                              />
                              {errors?.otherAwarenessChannel && <div className="text-danger fs-12">{errors.otherAwarenessChannel.message}</div>}
                            </div>
                          )}

                          {/* ── Country + City ── */}
                          <div className="row">
                            {missingFields.includes("country") && (
                              <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.locationGroup)}`}>
                                <label className="form-label font-w600">Country</label>
                                <input
                                  {...register("country")}
                                  type="text"
                                  className="form-control mb-1"
                                  placeholder="Type Your Country"
                                />
                                {errors?.country && <div className="text-danger fs-12">{errors.country.message}</div>}
                              </div>
                            )}
                            {missingFields.includes("city") && (
                              <div className={`form-group mb-3 ${getColumnWidth(missingFields, fieldGroups.locationGroup)}`}>
                                <label className="form-label font-w600">City</label>
                                <input
                                  {...register("city")}
                                  type="text"
                                  className="form-control mb-1"
                                  placeholder="Type Your City"
                                />
                                {errors?.city && <div className="text-danger fs-12">{errors.city.message}</div>}
                              </div>
                            )}
                          </div>

                          {/* ── Action Buttons ── */}
                          <div className="row">
                            <div className="form-group mt-4 col-md-6">
                              <button
                                className="btn btn-outline-primary btn-block position-relative overflow-hidden"
                                type="button"
                                disabled={clickLoading}
                                onClick={() => setOpenModal(false)}
                              >
                                {isRamadan && (
                                  <div className="skip-button"></div>
                                )}
                                {clickLoading ? (
                                  <>Loading <span className="spinner-grow spinner-grow-sm" style={{ marginLeft: "5px" }} role="status" aria-hidden="true"></span></>
                                ) : (
                                  <>Back</>
                                )}
                              </button>
                            </div>

                            <div className="form-group mt-4 col-md-6">
                              <button
                                className={`btn btn-primary btn-block position-relative overflow-hidden ${isRamadan ? "ramadan-button" : ""}`}
                                type="submit"
                                disabled={clickLoading}
                              >
                                {isRamadan && <div></div>}
                                {clickLoading ? (
                                  <>
                                    <span>Loading</span>
                                    <span className="spinner-grow spinner-grow-sm" style={{ marginLeft: "5px" }} role="status" aria-hidden="true"></span>
                                  </>
                                ) : (
                                  <span className="fs-15">{isRamadan ? <img src={carbon} alt="" style={{ width: "22px" }} /> : "C"}ontinue</span>
                                )}
                              </button>
                            </div>
                          </div>
                        </>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
}

export default CompleteAccountModal;
